import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { PageContainer } from "@/app/layouts/PageContainer";
import { FieldList, type FieldListItem } from "@/components/field-list";
import { DetailPage, type DetailTab } from "@/archetypes/DetailPage";
import { EmployeePicker } from "@/components/employee-picker";
import { TaskActivityLog } from "@/components/tasks/TaskActivityLog";
import { TaskHeader } from "@/components/tasks/TaskHeader";
import {
  tasksApi,
  type TaskDetailDto,
} from "@/lib/tasks";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { useAuth } from "@/app/auth-context";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Play, Check, RotateCcw, MoreHorizontal, Pencil } from "lucide-react";

// Inline edit (PATCH) deferred — lifecycle actions + display only for now.

const TERMINAL: ReadonlyArray<TaskDetailDto["status"]> = ["Completed", "Cancelled"];

function SkeletonDetail() {
  return (
    <PageContainer>
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 rounded bg-muted" />
        <div className="h-4 w-48 rounded bg-muted" />
        <div className="h-64 rounded bg-muted" />
      </div>
    </PageContainer>
  );
}

export function TaskDetailPage() {
  const { id } = useParams();
  const taskId = Number(id);
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const { me } = useAuth();

  const [completeOpen, setCompleteOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignId, setReassignId] = useState<number | null>(null);
  const [reassignLabel, setReassignLabel] = useState<string | null>(null);
  const [reassignReason, setReassignReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState("");

  const q = useQuery({
    queryKey: ["task", taskId],
    queryFn: () => tasksApi.get(taskId),
    enabled: !Number.isNaN(taskId),
  });

  // Shared invalidation/toast wiring for every lifecycle mutation.
  const onMutated = (msg: string) => {
    void queryClient.invalidateQueries({ queryKey: ["task", taskId] });
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    toast.success(msg);
  };
  const onErr = (err: Error) => toast.error(err.message);

  const startMut = useMutation({
    mutationFn: () => tasksApi.start(taskId),
    onSuccess: () => onMutated("Task started."),
    onError: onErr,
  });
  const completeMut = useMutation({
    mutationFn: (c: string | undefined) => tasksApi.complete(taskId, c),
    onSuccess: () => {
      onMutated("Task completed.");
      setCompleteOpen(false);
      setComment("");
    },
    onError: onErr,
  });
  const cancelMut = useMutation({
    mutationFn: (reason: string) => tasksApi.cancel(taskId, reason),
    onSuccess: () => {
      onMutated("Task cancelled.");
      setCancelOpen(false);
      setCancelReason("");
    },
    onError: onErr,
  });
  const reopenMut = useMutation({
    mutationFn: () => tasksApi.reopen(taskId),
    onSuccess: () => onMutated("Task reopened."),
    onError: onErr,
  });
  const reassignMut = useMutation({
    mutationFn: (args: { assigneeId: number; reason: string }) =>
      tasksApi.reassign(taskId, args.assigneeId, args.reason || undefined),
    onSuccess: () => {
      onMutated("Task reassigned.");
      setReassignOpen(false);
      setReassignId(null);
      setReassignLabel(null);
      setReassignReason("");
    },
    onError: onErr,
  });
  const commentMut = useMutation({
    mutationFn: (text: string) => tasksApi.comment(taskId, text),
    onSuccess: () => {
      onMutated("Comment added.");
      setCommentOpen(false);
      setCommentText("");
    },
    onError: onErr,
  });

  if (q.isLoading) return <SkeletonDetail />;
  if (q.isError || !q.data) {
    return (
      <PageContainer>
        <p className="p-6 text-sm text-muted-foreground">Task not found.</p>
      </PageContainer>
    );
  }

  const d: TaskDetailDto = q.data;
  const canWrite = has("tasks:write");
  const isTerminal = TERMINAL.includes(d.status);
  const isAdmin = me?.roles.includes("Admin") ?? false;

  // --- Header actions --------------------------------------------------------
  const primaryAction = (() => {
    if (!canWrite) return null;
    if (d.status === "Created" || d.status === "Assigned") {
      return (
        <Button size="sm" onClick={() => startMut.mutate()} disabled={startMut.isPending} className="gap-2 rounded-full px-5">
          <Play className="h-4 w-4" />
          {startMut.isPending ? "Starting…" : "Start"}
        </Button>
      );
    }
    if (d.status === "InProgress") {
      return (
        <Button size="sm" onClick={() => setCompleteOpen(true)} className="gap-2 rounded-full px-5">
          <Check className="h-4 w-4" />
          Complete
        </Button>
      );
    }
    if (d.status === "Completed" && isAdmin) {
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => reopenMut.mutate()}
          disabled={reopenMut.isPending}
          className="gap-2 rounded-full px-5"
        >
          <RotateCcw className="h-4 w-4" />
          {reopenMut.isPending ? "Reopening…" : "Reopen"}
        </Button>
      );
    }
    return null;
  })();

  const secondaryActions =
    canWrite && !isTerminal ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9 gap-2 bg-blue-50/80 border-blue-200 text-blue-900 hover:bg-blue-100 hover:text-blue-900 rounded-full">
            <MoreHorizontal className="h-4 w-4 text-blue-900/60" />
            <span className="sr-only">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setReassignOpen(true)}>
            Reassign
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setCommentOpen(true)}>
            Add comment
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive focus:bg-destructive/10"
            onClick={() => setCancelOpen(true)}
          >
            Cancel task
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  const headerActions = (
    <div className="flex items-center gap-2 pb-1.5">
      {primaryAction}
      {secondaryActions}
    </div>
  );

// Removed commonFields block since it is now rendered in the TaskHeader override.

  // Type-specific section.
  const typeFields: FieldListItem[] = [];
  switch (d.taskType) {
    case "Admin":
      typeFields.push({ label: "Follow-up kind", value: d.followUpKind ?? "—" });
      if (d.quoteId != null) {
        typeFields.push({
          label: "Quote",
          value: <Link to={`/quotes/${d.quoteId}`} className="text-primary hover:underline">Quote #{d.quoteId}</Link>,
        });
      }
      break;
    case "Badge":
      typeFields.push({ label: "Badge quantity", value: d.badgeQuantity ?? "—" });
      typeFields.push({ label: "Badge stage", value: d.badgeStage ?? "—" });
      break;
    case "Production":
      typeFields.push({ label: "Production stage", value: d.productionStage ?? "—" });
      typeFields.push({ label: "Expected minutes", value: d.expectedMinutes ?? "—" });
      break;
    case "Artwork":
      typeFields.push({ label: "Artwork action", value: d.artworkAction ?? "—" });
      typeFields.push({ label: "Job artwork", value: d.jobArtworkId ?? "—" });
      break;
  }

  const linkedFields: FieldListItem[] = [];
  if (d.jobId != null) {
    linkedFields.push({
      label: "Job",
      value: <Link to={`/jobs/${d.jobId}`} className="text-primary hover:underline">#{d.jobReferenceNo ?? d.jobId}</Link>,
    });
  }
  if (d.quoteId != null) {
    linkedFields.push({
      label: "Quote",
      value: <Link to={`/quotes/${d.quoteId}`} className="text-primary hover:underline">Quote #{d.quoteId}</Link>,
    });
  }

  const detailsContent = (
    <div className="space-y-6">
      {(typeFields.length > 0 || linkedFields.length === 0) && (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">{d.taskType} details</h3>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-slate-600" title={`Edit ${d.taskType} details`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
            <FieldList items={typeFields} />
          </CardContent>
        </Card>
      )}
      {linkedFields.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Linked</h3>
            <FieldList items={linkedFields} />
          </CardContent>
        </Card>
      )}
    </div>
  );

  const tabs: DetailTab[] = [
    { id: "details", label: "Details", content: detailsContent },
    { id: "activity", label: "Activity", content: <TaskActivityLog history={d.history} /> },
  ];

  return (
    <>
      <DetailPage
        title={`T-${d.id} · ${d.title}`}
        headerOverride={<TaskHeader task={d} />}
        tabActions={headerActions}
        backTo="/tasks"
        tabs={tabs}
        defaultTab="details"
      />

      {/* Complete (optional comment) */}
      <Dialog open={completeOpen} onOpenChange={(o) => { if (!o) { setCompleteOpen(false); setComment(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete task</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="complete-comment">Comment (optional)</Label>
            <Textarea
              id="complete-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompleteOpen(false); setComment(""); }}>
              Cancel
            </Button>
            <Button
              disabled={completeMut.isPending}
              onClick={() => completeMut.mutate(comment.trim() || undefined)}
            >
              {completeMut.isPending ? "Completing…" : "Complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign */}
      <Dialog open={reassignOpen} onOpenChange={(o) => { if (!o) { setReassignOpen(false); setReassignId(null); setReassignLabel(null); setReassignReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Assignee</Label>
              <EmployeePicker
                value={reassignId}
                valueLabel={reassignLabel}
                onChange={(id, emp) => {
                  setReassignId(id);
                  setReassignLabel(emp?.fullName ?? null);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="reassign-reason">Reason (optional)</Label>
              <Textarea
                id="reassign-reason"
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReassignOpen(false); setReassignId(null); setReassignLabel(null); setReassignReason(""); }}>
              Cancel
            </Button>
            <Button
              disabled={reassignId == null || reassignMut.isPending}
              onClick={() => reassignId != null && reassignMut.mutate({ assigneeId: reassignId, reason: reassignReason.trim() })}
            >
              {reassignMut.isPending ? "Reassigning…" : "Reassign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel (required reason) */}
      <Dialog open={cancelOpen} onOpenChange={(o) => { if (!o) { setCancelOpen(false); setCancelReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel task</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Reason (required)</Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelOpen(false); setCancelReason(""); }}>
              Back
            </Button>
            <Button
              variant="destructive"
              disabled={cancelReason.trim() === "" || cancelMut.isPending}
              onClick={() => cancelMut.mutate(cancelReason.trim())}
            >
              {cancelMut.isPending ? "Cancelling…" : "Cancel task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add comment (required text) */}
      <Dialog open={commentOpen} onOpenChange={(o) => { if (!o) { setCommentOpen(false); setCommentText(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add comment</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="comment-text">Comment (required)</Label>
            <Textarea
              id="comment-text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCommentOpen(false); setCommentText(""); }}>
              Cancel
            </Button>
            <Button
              disabled={commentText.trim() === "" || commentMut.isPending}
              onClick={() => commentMut.mutate(commentText.trim())}
            >
              {commentMut.isPending ? "Adding…" : "Add comment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
